<template>
  <div style="height: 100%">
    <list-form
      ref="list-form"
      class="activity-review-list-form"
      :config="config"
      :formModel="formModel"
    >
      <template v-slot="{ data, item, autoSave }">
        <activity-review :data="data" :item="item" :model="formModel" :autoSave="autoSave" @onDetails="handleDetails"/>
      </template>
    </list-form>
    <dialog-status-repair-order />
    <dialog-new-instruction-repair-order />
    <dialog-status-customer />
    <dialog-new-instruction-customer />
    <dialog-stock-card
      ref="dialog-stock-card"
    ></dialog-stock-card>
    <dialog-connect-po
      ref="dialog-connect-po"
    ></dialog-connect-po>
    <dialog-connect-wo
      ref="dialog-connect-wo"
    ></dialog-connect-wo>
    <dialog-activity-item
      :config="{ id: reviewDialogId, api: 'RepairExchangeOrderFollowUp' }"
      @onClose="handleClose"
      @onConnectPO="handlerConnectPO"
      @onConnectWO="handlerConnectWO"
      @onStockCard="handlerStockCard" />
  </div>
</template>

<script>
import { config as listFormConfig } from './ListFormConfig.js'
import { waitValue } from '@/utils/promises'
import { MainModel } from '@/models/CustomerPOFollowUp'
import { ServiceCustomerPOFollowUp } from '@/services/CustomerPOFollowUp'
import * as dynamic from '@/utils/dynamic'
import ExcelExporter from '@/components/ExcelExporter'
export default {
  name: 'SupportCustomerPoFollowupReview',
  components: {
    'activity-review': () => import(/* webpackChunkName: 'SupportCustomerPoFollowupReview' */ './ActivityReview'),
    'list-form': () => import(/* webpackChunkName: 'ListForm' */ '@/components/ListForm/Index'),
    'dialog-status-repair-order': () => import(/* webpackChunkName: 'DialogStatusRepairOrder' */ '@/components/Shared/ActivityDialogStatusEmailRepairOrder'),
    'dialog-new-instruction-repair-order': () => import(/* webpackChunkName: 'DialogNewInstructionRepairOrder' */ '@/components/Shared/ActivityDialogNewInstructionRepairOrder'),
    'dialog-new-instruction-customer': () => import(/* webpackChunkName: 'DialogNewInstructionCustomer' */ '@/components/Shared/ActivityDialogNewInstructionCustomer'),
    'dialog-status-customer': () => import(/* webpackChunkName: 'ActivityDialogStatusEmailCustomer' */ '@/components/Shared/ActivityDialogStatusEmailCustomer'),
    'dialog-stock-card': () => import(/* webpackChunkName: 'DialogStockCard' */ '@/components/Shared/Activity/DialogStockCard'),
    'dialog-connect-po': () => import(/* webpackChunkName: 'DialogConnectPo' */ '@/components/Shared/ActivityDialogConnectToOpenPO/Index'),
    'dialog-connect-wo': () => import(/* webpackChunkName: 'DialogConnectWo' */ '@/components/Shared/ActivityDialogConnectToOpenWO/Index'),
    'dialog-activity-item': () => import(/* webpackChunkName: 'SupportCustomerPoFollowupReview' */ './DialogActivityItem')
  },
  created () {
    this.$bus.$on('customer-po-followup-on-filter', this.handlerFilter)
    this.$bus.$on('customer-po-followup-review-on-export', this.handlerExport)
  },
  beforeDestroy () {
    this.$bus.$off('customer-po-followup-on-filter', this.handlerFilter)
    this.$bus.$off('customer-po-followup-review-on-export', this.handlerExport)
  },
  props: {
    orderBy: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      reviewDialogId: 'review-open-po'
    }
  },
  watch: {
    orderBy () {
      this.getListForm()
        .then(list => list.loadData())
    }
  },
  computed: {
    config () {
      return listFormConfig(this.orderBy)
    }
  },
  methods: {
    formModel () {
      return new MainModel()
    },
    handleDetails ({ id }) {
      const query = { [this.reviewDialogId]: `edit[${id}]` }
      this.$router.replace({ ...this.$router.currentRoute, query })
    },
    getListForm () {
      return waitValue(() => this.$refs['list-form'])
    },
    handlerFilter (filterValue) {
      this.getListForm()
        .then((list) => {
          list.setFilters(filterValue)
          list.loadData()
        })
    },
    handleClose () {
      return this.getListForm()
        .then((listForm) => {
          listForm.refresh()
        })
    },
    handlerConnectPO ({ rfqid }) {
      this.$refs['dialog-connect-po'].show({ rfqid })
    },
    handlerConnectWO ({ rfqid }) {
      this.$refs['dialog-connect-wo'].show({ rfqid })
    },
    handlerStockCard () {
      this.$refs['dialog-stock-card'].show(...arguments)
    },
    async handlerExport ({ isAll }) {
      const list = await this.getListForm()

      list && list.setLoading(true)

      try {
        const { params } = list.createGetRequest()

        const { data: { value: records } } = await ServiceCustomerPOFollowUp.getAllDynamic({
          ...params,
          select: dynamic
            .select(
              'rfq3.rfq.customerCompany.name as customerCompanyName',
              'po', 'partSN', 'part.partNumber as partNumber',
              'orderPhase.title as orderPhaseTitle',
              'exchAgreementSigned', 'unitShipped', 'coreReceived'),
          ...(isAll ? { take: undefined, skip: undefined } : {})
        })

        const columns = [
          { title: this.L10n('customer'), field: 'customerCompanyName' },
          { title: this.L10n('po'), field: 'po' },
          { title: this.L10n('part-number'), field: 'partNumber' },
          { title: this.L10n('part-SN'), field: 'partSN' },
          { title: this.L10n('order-phase'), field: 'orderPhaseTitle' },
          { title: this.L10n('exch-agreement-signed'), field: 'exchAgreementSigned', type: 'b' },
          { title: this.L10n('unit-shipped'), field: 'unitShipped', type: 'b' },
          { title: this.L10n('core-received'), field: 'coreReceived', type: 'b' }
        ].map((column) => ({
          ...column,
          width: Math.max(column.title.length, ...records.map((record) => record[column.field] ? String(record[column.field]).length : 0))
        }))

        await ExcelExporter.export(this.L10n('customer-po-follow-up-review'), columns, records)
      } catch (e) {
        this.requestErrorHandler(e)
      } finally {
        list && list.setLoading(false)
      }
    }
  }
}
</script>
